import {
  ComponentProps,
  RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { Input } from '@components/Input/Input';
import { APIProviderContext, useMapsLibrary } from '@vis.gl/react-google-maps';

// Referenced from google's official typescript example for places autocomplete: https://developers.google.com/maps/documentation/javascript/examples/rgm-autocomplete
interface GooglePlaceAutocompleteProps extends ComponentProps<typeof Input> {
  inputRef: RefObject<HTMLInputElement>;
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
}
export function GooglePlaceAutocomplete({
  inputRef,
  onPlaceSelected,
  ...props
}: GooglePlaceAutocompleteProps): JSX.Element {
  // avoid the need to memoize the onPlaceSelected prop
  const latestOnPlaceSelected = useRef(onPlaceSelected);
  useLayoutEffect(() => {
    latestOnPlaceSelected.current = onPlaceSelected;
  });

  const ctx = useContext(APIProviderContext);

  if (!ctx) {
    console.error('GooglePlaceAutocomplete must be wrapped in an APIProvider');
  }

  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places || !inputRef.current) {
      return;
    }

    const widget = new places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'us' },
      types: ['address'],
    });

    widget.addListener('place_changed', () => {
      const place = widget.getPlace();
      latestOnPlaceSelected.current(place);
    });

    // NOTE: This is a workaround to override the autocomplete value being set to
    //       "off" by the google places library. "off" is ignored by Chrome.
    setTimeout(() => {
      // NOTE: typescript wants to restrict this to well-known values like
      //       address, new-password, etc. Other descriptive values are still
      //       valid and effective at preventing erroneous autocomplete. I
      //       would like to avoid other bad autofill values like passwords.
      if (inputRef.current) {
        // @ts-expect-error allow other autocomplete string values
        inputRef.current.autocomplete = 'search';
      }
    }, 50);
  }, [inputRef, places]);

  return <Input type="text" ref={inputRef} autoComplete="search" {...props} />;
}
